/**
 * Reusable confirmation dialog for destructive actions
 */
import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { colors as theme } from '../theme';

const colors = {
  primary: theme.gold,
  danger: theme.danger,
  bg: theme.bg,
  bgCard: theme.surface1,
  border: theme.borderStrong,
  text: theme.text,
  textMuted: theme.textMuted,
};

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message = 'This action cannot be undone.',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger', // 'danger' | 'warning' | 'default'
  loading = false, // NEW: loading state for async operations
}) => {
  if (!isOpen) return null;

  const variantColors = {
    danger: colors.danger,
    warning: theme.warning,
    default: colors.primary,
  };

  const accentColor = variantColors[variant] || variantColors.default;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
      }}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '14px',
          border: `1px solid ${colors.border}`,
          maxWidth: '400px',
          width: '100%',
          padding: '24px',
          position: 'relative',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={loading}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: loading ? 0.5 : 1,
          }}
        >
          <X size={20} color={colors.textMuted} />
        </button>

        {/* Icon */}
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: `${accentColor}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px',
          }}
        >
          <AlertTriangle size={24} color={accentColor} />
        </div>

        {/* Title */}
        <h3
          style={{
            fontSize: '18px',
            fontWeight: 600,
            color: colors.text,
            margin: '0 0 8px 0',
          }}
        >
          {title}
        </h3>

        {/* Message */}
        <p
          style={{
            fontSize: '14px',
            color: colors.textMuted,
            margin: '0 0 24px 0',
            lineHeight: 1.5,
          }}
        >
          {message}
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: colors.bg,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.text,
              fontSize: '14px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={async () => {
              await onConfirm();
            }}
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: accentColor,
              border: 'none',
              borderRadius: '8px',
              color: variant === 'danger' ? theme.text : theme.bg,
              fontSize: '14px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
